import { Big } from 'big.js';
import moment from 'moment';
import { PropTypes } from 'prop-types';
import React, { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { hideDialog } from '../../../core/actions';
import { generateToastr } from '../../../core/toastMessages';
import Loader from '../../../core/components/Loader';
import ModalDialog from '../../../core/components/Modal';
import { formatNumber, parseNumber } from '../../../utils/number-util';
import UtilityEditForm from '../components/UtilityEditForm';
import { utilityHelper } from '../../expenses/helpers';
import { selectSite } from '../../../store/authReducer';
import {
  defaultUnits,
  useGetUtilityQuery,
  useGetUtilityUnitsQuery,
  useAddUtilityMutation,
  useUpdateUtilityMutation,
} from '../api';

const initialFormValues = {
  type: 0,
  name: null,
  start: null,
  end: null,
  startReading: null,
  endReading: null,
  unit: '',
  cost: null,
  currency: 'USD',
  resourceLink: '',
};

const datesAreOutOfOrder = (start, end) => {
  if (!start || !end) return false;
  return moment(start).isAfter(moment(end), 'day');
};

const getInvalidLineItemReading = (lineItems) =>
  (lineItems || []).find((li) => {
    if (!li || (!li.name && !li.amount && !li.description && !li.bop && !li.eop)) return false;

    const startReading = parseNumber(li.bop);
    const endReading = parseNumber(li.eop);

    return Number.isNaN(startReading) || Number.isNaN(endReading) || startReading > endReading;
  });

const UpsertUtilityDialog = ({ isOpen, isNew, title, tabType, utilityId }) => {
  const dispatch = useDispatch();
  const siteId = useSelector(selectSite);
  const formRef = useRef(null);

  const { data: utilityUnitsData } = useGetUtilityUnitsQuery({ siteId });
  const utilityUnits = utilityUnitsData ?? defaultUnits;

  const { data: utilityInEditRaw, isLoading: isLoadingUtility } = useGetUtilityQuery(
    { id: utilityId, siteId },
    { skip: isNew || !utilityId }
  );

  const [addUtility] = useAddUtilityMutation();
  const [updateUtility] = useUpdateUtilityMutation();

  const handleClose = () => dispatch(hideDialog());

  const onReady = () => {
    if (formRef.current?.submit) {
      formRef.current.submit();
    }
  };

  const onSubmit = async (values) => {
    const endReading = parseNumber(values.endReading);
    const startReading = parseNumber(values.startReading);
    if (datesAreOutOfOrder(values.start, values.end)) {
      generateToastr('error', 'Start date must be before or equal to end date');
      return;
    }
    if (Number.isNaN(startReading) || Number.isNaN(endReading)) {
      generateToastr('error', 'Please enter numeric readings for BOP and EOP');
      return;
    }
    if (endReading < startReading) {
      generateToastr('error', 'End reading must be greater than or equal to start reading');
      return;
    }
    if (values.unit === undefined || values.unit === null || values.unit === '') {
      generateToastr('error', 'Please select a reading unit');
      return;
    }
    const utilityTypeKey = utilityHelper.utilityTypeEnumKeyFromVal(values.type);
    const utilityUnit = utilityHelper.utilityUnitEnumKeyFromVal(
      values.unit,
      utilityTypeKey,
      utilityUnits
    );

    const formatDate = (v) => {
      if (!v) return null;
      if (moment.isMoment(v)) return v.format('YYYY-MM-DD');
      return moment(v).format('YYYY-MM-DD');
    };

    const usageFixed = Big(endReading).minus(startReading).toFixed(4);
    const usage = usageFixed.replace(/(\.\d*?)0+$/, '$1').replace(/\.$/, '');

    const rawItems = Array.isArray(values.lineItems) ? values.lineItems : [];
    const invalidLineItemReading = getInvalidLineItemReading(rawItems);
    if (invalidLineItemReading) {
      generateToastr(
        'error',
        'Line item end reading must be greater than or equal to start reading'
      );
      return;
    }

    const items = rawItems
      .filter((li) => li && (li.name || li.amount || li.description || li.bop || li.eop))
      .map((li) => {
        let liTypeKey = utilityTypeKey;
        if (li && li.type !== undefined && li.type !== null && li.type !== '') {
          if (typeof li.type === 'number') {
            liTypeKey = utilityHelper.utilityTypeEnumKeyFromVal(li.type);
          } else if (typeof li.type === 'string') {
            liTypeKey = li.type;
          }
        }
        return {
          name: li.name ? String(li.name).trim() : '',
          description: li.description ? String(li.description).trim() : '',
          startReading: `${parseNumber(li.bop)}`,
          endReading: `${parseNumber(li.eop)}`,
          cost: `${parseNumber(li.amount)}`,
          type: liTypeKey,
        };
      });

    const allValues = {
      ...values,
      type: utilityTypeKey,
      name: values?.name || '',
      start: formatDate(values.start),
      end: formatDate(values.end),
      startReading: `${startReading}`,
      endReading: `${endReading}`,
      unit: utilityUnit,
      cost: `${parseNumber(values.cost)}`,
      usage: `${usage}`,
      lineItems: items,
      resourceLink: values?.resourceLink ? String(values.resourceLink).trim() : '',
    };

    try {
      if (isNew) {
        await addUtility(allValues).unwrap();
      } else {
        await updateUtility({ id: utilityId, ...allValues }).unwrap();
      }
      dispatch(hideDialog());
    } catch {
      generateToastr('error', 'Failed to save utility');
    }
  };

  const getRenderableUtilityUnits = (utilityType, utilityUnit) => {
    let renderableUtilityUnits = { ...utilityUnits };
    if (utilityUnit === undefined || utilityUnit === null || utilityUnit === '') {
      return renderableUtilityUnits;
    }
    // Set of available units might have changed. Making sure the original unit is available.
    renderableUtilityUnits = utilityHelper.ensureCurrentUnitExists(
      renderableUtilityUnits,
      utilityType,
      utilityUnit
    );
    return renderableUtilityUnits;
  };

  const getRenderableInitialUtility = (initialUtility, rendableUtilityUnits) => {
    const renderableUtility = { ...initialUtility };
    renderableUtility.startReading = formatNumber(initialUtility.startReading, 4, true);
    renderableUtility.endReading = formatNumber(initialUtility.endReading, 4, true);
    renderableUtility.cost = formatNumber(initialUtility.cost, 2);
    // existing utility type and unit are string but form field values needs to be integer equivalent
    renderableUtility.unit =
      initialUtility.unit === undefined ||
      initialUtility.unit === null ||
      initialUtility.unit === ''
        ? ''
        : utilityHelper.utilityUnitEnumValFromKey(
            initialUtility.unit,
            initialUtility.type,
            rendableUtilityUnits
          );
    renderableUtility.type = utilityHelper.utilityTypeEnumValFromKey(initialUtility.type);
    renderableUtility.lineItems = Array.isArray(initialUtility.lineItems)
      ? initialUtility.lineItems
      : [];
    return renderableUtility;
  };

  if (!isNew && (!utilityInEditRaw || isLoadingUtility)) {
    return (
      <ModalDialog
        title={title}
        isOpen={isOpen}
        close={handleClose}
        className="enhanced-dialog narrow padded-minimal"
      >
        <Loader />
      </ModalDialog>
    );
  }

  let initialUtility = isNew ? { ...initialFormValues } : { ...utilityInEditRaw };
  if (isNew && tabType) {
    initialUtility.type = utilityHelper.utilityTypeEnumValFromKey(tabType);
  }

  const renderableUtilityUnits = getRenderableUtilityUnits(
    initialUtility.type,
    initialUtility.unit
  );
  const renderableUtility = getRenderableInitialUtility(initialUtility, renderableUtilityUnits);

  return (
    <ModalDialog
      title={title}
      onReady={onReady}
      isOpen={isOpen}
      close={handleClose}
      className="enhanced-dialog narrow padded-minimal"
    >
      <UtilityEditForm
        ref={formRef}
        onSubmit={onSubmit}
        utilityUnits={renderableUtilityUnits}
        initialValues={{ ...renderableUtility }}
        enableReinitialize={!isNew}
        tabType={tabType}
        currentUtility={tabType ?? 'Water'}
      />
    </ModalDialog>
  );
};

UpsertUtilityDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  isNew: PropTypes.bool,
  title: PropTypes.string.isRequired,
  tabType: PropTypes.string,
  utilityId: PropTypes.number,
};

export default UpsertUtilityDialog;
